import { useState, useEffect } from "react";
import * as OBL from "./affinity_obligations_api";
import * as OUT from "./affinity_output";
import { flagFor } from "./affinity_offices";
import { getDatasets, isConfigured } from "./affinity_ops_api";
import { messageFor } from "./affinity_action";
const CY = "#00C4CC";
const NAVY = "#001242";

const Badge = ({ label, colors }) => (
  <span style={{ display:"inline-block", padding:"2px 9px", borderRadius:20, fontSize:10, fontWeight:600, background:colors?.bg||"#eee", color:colors?.color||"#333", whiteSpace:"nowrap" }}>{label}</span>
);

// Flags come from the shared offices list rather than being repeated here.
const FLAGS = { IOM: flagFor("Isle of Man"), Malta: flagFor("Malta"),
                Cayman: flagFor("Cayman Islands"), Cyprus: flagFor("Cyprus"),
                UK: flagFor("United Kingdom"), USA: flagFor("United States") };

// Obligations now come from the DATABASE (db/081), not from this constant.
// They were hardcoded here, which meant Malta and Cayman's schedules could not
// be corrected and the other four jurisdictions could not be filled in at all.
// JUR_INFO keeps the regulator, legislation and licence — those are stable
// reference data — and the obligations arrays are no longer read.
// The module keys jurisdictions by name; the database keys them by code.
const LOC_CODE = { IOM:"IOM", Cayman:"CYM", Malta:"MALTA", Cyprus:"CYPRUS",
                   UK:"UK", USA:"USA" };

const JUR_INFO = {
  IOM: {
    name: "Isle of Man",
    regulator: "Isle of Man Financial Services Authority (IOMFSA)",
    legislation: ["Companies Acts 1931-2004", "Companies Act 2006", "Trustee Act 2001",
                  "Financial Services Act 2008", "Anti-Money Laundering and Countering the Financing of Terrorism Code 2019",
                  "Beneficial Ownership Act 2017"],
    obligations: [],
    pending: "Obligation schedule to be confirmed by Compliance. Regulator and legislation are stated; filing deadlines are deliberately not pre-filled, because a wrong date in a compliance tracker is worse than a visibly empty one.",
  },
  Cayman: {
    name: "Cayman Islands",
    regulator: "Cayman Islands Monetary Authority (CIMA)",
    legislation: ["Companies Law (2023 Revision)", "Trusts Law (2021 Revision)", "Anti-Money Laundering Regulations (2024)", "Beneficial Ownership Transparency Law 2023", "International Tax Co-operation (ESR) Law 2018", "Mutual Funds Law (2023)", "Securities Investment Business Law (2020)"],
    obligations: [
      { id:1,  area:"AML/CFT",        title:"AML policies & procedures",               freq:"Annual review", due:"31/12/2025", status:"On track",  owner:"Colette Grisdale" },
      { id:2,  area:"AML/CFT",        title:"Risk assessment — ML/TF",                 freq:"Annual",        due:"31/12/2025", status:"On track",  owner:"Colette Grisdale" },
      { id:3,  area:"AEOI",           title:"FATCA return — CIMA portal",              freq:"Annual",        due:"31/07/2025", status:"Overdue",   owner:"Garry Crossan" },
      { id:4,  area:"AEOI",           title:"CRS return — CIMA portal",                freq:"Annual",        due:"31/07/2025", status:"Overdue",   owner:"Garry Crossan" },
      { id:5,  area:"Substance",      title:"ESR return — all in-scope entities",      freq:"Annual",        due:"12 months after year end", status:"On track",  owner:"Garry Crossan" },
      { id:6,  area:"BO register",    title:"Beneficial ownership register — CIMA",    freq:"On change",     due:"Ongoing",    status:"Current",   owner:"Garry Crossan" },
      { id:7,  area:"Annual returns", title:"Annual returns — Registrar of Companies", freq:"Annual",        due:"31/01/2026", status:"On track",  owner:"Garry Crossan" },
      { id:8,  area:"Funds",          title:"Mutual Fund annual return",                freq:"Annual",        due:"30/06/2025", status:"Overdue",   owner:"Garry Crossan" },
    ],
    entities: [
      { name:"Caledonian Ventures Ltd",   type:"Exempted Co",  risk:"Medium", administrator:"Garry Crossan", issues:0 },
      { name:"Pacific Wealth Trust",      type:"STAR Trust",   risk:"High",   administrator:"Garry Crossan", issues:1 },
      { name:"Apex Growth Fund Ltd",      type:"Exempted Co",  risk:"Very High",administrator:"Garry Crossan",issues:2 },
      { name:"Bluewater Family Trust",    type:"Ordinary Trust",risk:"Medium",administrator:"Garry Crossan", issues:0 },
      { name:"Riviera Trust",             type:"STAR Trust",   risk:"Medium", administrator:"Garry Crossan", issues:0 },
    ],
    amlKey: [
      ["MLRO",                "Colette Grisdale (Head of Compliance)"],
      ["DMLRO — Cayman",      "Garry Crossan"],
      ["Screening provider",  "Worldcheck"],
      ["STR filing body",     "Financial Reporting Authority (FRA)"],
      ["Review cycles",       "VH: 6mo · H: 12mo · M: 18mo · S: 24mo"],
      ["EDD threshold",       "All VH + High risk + all PEPs"],
    ],
  },
  Malta: {
    name: "Malta",
    regulator: "Malta Financial Services Authority (MFSA)",
    legislation: ["Companies Act (Cap. 386)", "Foundations (Properties) Act", "Financial Intelligence Analysis Unit (FIAU) Regulations", "Prevention of Money Laundering Act (PMLA)", "Beneficial Ownership Registration Regulations 2020", "MFSA Conduct of Business Rulebook"],
    obligations: [
      { id:1,  area:"CSP licence",    title:"Authorisation as Trustee / Administrator",  freq:"Ongoing",  due:"30/09/2025", status:"Active",    owner:"Joanne Fenech" },
      { id:2,  area:"AML/CFT",        title:"Business risk assessment",                   freq:"Annual",   due:"31/12/2025", status:"On track",  owner:"Colette Grisdale" },
      { id:3,  area:"AML/CFT",        title:"FIAU sectoral risk assessment update",       freq:"Annual",   due:"31/12/2025", status:"On track",  owner:"Colette Grisdale" },
      { id:4,  area:"AEOI",           title:"CRS/FATCA return — MFSA portal",            freq:"Annual",   due:"31/07/2025", status:"Overdue",   owner:"Joanne Fenech" },
      { id:5,  area:"BO register",    title:"Beneficial ownership register — MFSA BROS", freq:"On change",due:"Ongoing",    status:"Current",   owner:"Joanne Fenech" },
      { id:6,  area:"Annual returns", title:"Annual returns — Malta Business Registry",   freq:"Annual",   due:"Within 42 days of anniversary", status:"On track", owner:"Joanne Fenech" },
      { id:7,  area:"Reporting",      title:"FIAU supervision annual report",             freq:"Annual",   due:"30/04/2026", status:"On track",  owner:"Colette Grisdale" },
      { id:8,  area:"Data protection","title":"Data Protection Officer annual review",   freq:"Annual",   due:"31/10/2025", status:"On track",  owner:"Joanne Fenech" },
    ],
    entities: [
      { name:"Azure Mediterranean Fdn",   type:"Foundation",   risk:"Low",    administrator:"Joanne Fenech", issues:0 },
      { name:"Stonebridge Capital Ltd",    type:"Private Ltd",  risk:"Low",    administrator:"Joanne Fenech", issues:1 },
      { name:"Malta Ventures",             type:"Company",      risk:"Medium", administrator:"Joanne Fenech", issues:2, note:"Unregulated entity — AML/KYC pending" },
      { name:"Verano Maritime SA (pend.)", type:"Company",      risk:"Low",    administrator:"Joanne Fenech", issues:0 },
    ],
    amlKey: [
      ["MLRO",                 "Colette Grisdale (Head of Compliance)"],
      ["DMLRO — Malta",        "Joanne Fenech"],
      ["Screening provider",   "Worldcheck"],
      ["STR filing body",      "Financial Intelligence Analysis Unit (FIAU)"],
      ["Review cycles",        "VH: 6mo · H: 12mo · M: 18mo · S: 24mo"],
      ["EDD threshold",        "All VH + High risk + all PEPs"],
    ],
  },
  Cyprus: {
    name: "Cyprus",
    regulator: "Cyprus Securities and Exchange Commission (CySEC) / Registrar of Companies",
    legislation: ["Companies Law Cap. 113", "Prevention and Suppression of Money Laundering Laws 2007-2021",
                  "Administrative Service Providers Law 2012", "Beneficial Ownership Register (Directive 2018/843)"],
    obligations: [],
    // The ASP licensing position is now confirmed: Affinity is licensed by
    // CySEC to provide corporate services, so the Cyprus office is a
    // supervised Administrative Service Provider. The obligation schedule is
    // still outstanding.
    licence: "Licensed by CySEC as an Administrative Service Provider (corporate services)",
    pending: "Obligation schedule still to be confirmed by Compliance. The CySEC ASP licence is confirmed.",
  },
  UK: {
    name: "United Kingdom",
    regulator: "Companies House / HMRC",
    legislation: ["Companies Act 2006", "Money Laundering Regulations 2017 (as amended)",
                  "Economic Crime and Corporate Transparency Act 2023", "Trust Registration Service requirements"],
    obligations: [],
    pending: "Obligation schedule to be confirmed by Compliance.",
  },
  USA: {
    name: "United States",
    regulator: "State registries (South Dakota, Florida) / FinCEN / IRS",
    legislation: ["Corporate Transparency Act (FinCEN beneficial ownership reporting)",
                  "State trust and LLC statutes", "FATCA (IRS)"],
    obligations: [],
    pending: "Two entities in different states with different requirements. Obligation schedule to be confirmed by Compliance.",
  },
};

const statusC = {
  "On track":  { bg:"#EAF3DE", color:"#27500A" },
  "Active":    { bg:"#EAF3DE", color:"#27500A" },
  "Current":   { bg:"#EAF3DE", color:"#27500A" },
  "Overdue":   { bg:"#FCEBEB", color:"#A32D2D" },
  "Due soon":  { bg:"#FAEEDA", color:"#633806" },
};

const th = { padding:"8px 12px", textAlign:"left", fontSize:10, fontWeight:600, color:"#666", textTransform:"uppercase", letterSpacing:"0.4px", borderBottom:"0.5px solid #e5e5e5", background:"#f9f9f9" };
const td = { padding:"9px 12px", fontSize:11, borderBottom:"0.5px solid #e5e5e5" };

export default function AffinityJurisdictionCompliance({ onNav }) {
  // ── Output plumbing ───────────────────────────────────────────────────────
  const [outMsg, setOutMsg] = useState("");
  const outRun = (fn) => {
    try {
      const res = fn();
      if (res && res.ok === false) { setOutMsg(res.error || "That could not be produced."); return false; }
      setOutMsg("");
      return true;
    } catch (e) { setOutMsg(String((e && e.message) || e)); return false; }
  };

  const openPortal = () => outRun(() => OUT.openRegulatorPortal(data && data.name));

  // The tile is narrow, so the acronym where the record gives one in brackets,
  // otherwise the first part before a slash. Never a guess.
  const shortRegulator = (full) => {
    if (!full) return "—";
    const m = full.match(/\(([A-Z]{3,8})\)/);
    if (m) return m[1];
    return full.split("/")[0].trim();
  };

  const [liveJ,setLiveJ]=useState(null);
  useEffect(()=>{ if(!isConfigured) return; let ok=true; getDatasets("jur.").then(({data})=>{ if(ok && Array.isArray(data)){ const r=data.find(x=>x.dkey==="jur.info"); if(r)setLiveJ(r.data);} }).catch(()=>{}); return ()=>{ok=false;}; },[]);
  const [jur, setJur]   = useState("Cayman");
  const [view, setView] = useState("overview");
  const [modal, setModal] = useState(null);

  const nb  = { padding:"5px 12px", fontSize:11, borderRadius:5, border:"0.5px solid #e5e5e5", background:"transparent", color:"#666", cursor:"pointer" };
  const nba = { ...nb, background:CY, color:"#fff", border:`0.5px solid ${CY}`, fontWeight:500 };

  // A jurisdiction may be selected that the live list does not carry, and a new
  // office has no obligation schedule yet — neither should throw.
  const raw = ((liveJ||JUR_INFO)[jur]) || JUR_INFO[Object.keys(JUR_INFO)[0]];
  // A newly added office carries a regulator and legislation but no schedule,
  // entities or AML matrix yet. Default every list so the page renders rather
  // than throwing on the first missing one.
  const data = { obligations: [], entities: [], amlKey: [], legislation: [], ...raw };
  // Live from the database, falling back to nothing rather than to the old
  // constant — a stale hardcoded deadline is exactly what this replaced.
  const [liveObs, setLiveObs] = useState(null);
  const [obsMsg, setObsMsg]   = useState("");
  const [obForm, setObForm]   = useState(false);
  const [ob, setOb]           = useState({});

  const loadObs = async () => {
    setObsMsg("");
    const r = await OBL.obligationsList(LOC_CODE[jur] || null, false);
    if (!r.live) { setObsMsg("Not signed in — obligations are read from the database."); return; }
    setLiveObs(r.data || []);
    if (!r.ok) setObsMsg(r.error);
  };
  useEffect(() => { loadObs(); }, [jur]);   // eslint-disable-line

  const obligations = (liveObs || []).map((o) => ({
    id: o.id, area: o.area_name, title: o.title,
    freq: o.frequency || o.due_description,
    due: o.due_description, owner: o.owner || "—",
    // Nothing is called "On track" unless Compliance has confirmed it. An
    // unconfirmed deadline came from a migration or a first draft and should
    // not read as something anyone has checked.
    status: o.confirmed ? "On track" : "Unconfirmed",
    confirmed: o.confirmed, legislation: o.legislation_ref, route: o.filing_route,
  }));
  const overdueCount = obligations.filter(o=>o.status==="Overdue").length;

  return (
    <div style={{ fontFamily:"'Catamaran',system-ui,sans-serif", background:"#f8f9fc", color:"#111", minHeight:"100vh" }}>
      {outMsg && (
        <div style={{ margin:"0 20px 10px", padding:"9px 12px", borderRadius:7, fontSize:11.5,
                      lineHeight:1.6, background:"#FCEBEB", border:"0.5px solid #f0c9c9", color:"#A32D2D" }}>
          {outMsg}
        </div>
      )}

      <div style={{ background:NAVY, padding:"12px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          
          <span style={{ color:"#8892b0", fontSize:13 }}>Jurisdiction Compliance</span>
        </div>
        <div style={{ display:"flex", gap:5 }}>
          {["Entity Admin","Compliance","Statutory"].map(n=><button key={n} style={{ ...nb, color:"#8892b0", borderColor:"#334" }}>{n}</button>)}
          <button style={nba} disabled disabled aria-current="page" title="You are already on this page">Jurisdiction</button>
        </div>
      </div>

      {/* Jurisdiction switcher */}
      <div style={{ background:"#fff", borderBottom:"0.5px solid #e5e5e5", padding:"10px 24px", display:"flex", gap:8, alignItems:"center" }}>
        {/* "Our office should be found using a drop down / search bar." A row
            of buttons is fine for three and wraps badly on a phone at six,
            which is what Affinity has. Each office keeps its own Overview,
            Obligations, AML framework and Legislation — the picker chooses
            whose they are. */}
        <span style={{ fontSize:11, color:"#666", marginRight:4 }}>Our office:</span>
        <select value={jur} onChange={(e)=>setJur(e.target.value)}
                style={{ padding:"6px 10px", borderRadius:6, border:"0.5px solid #ccc",
                         background:"#fff", fontSize:12, fontWeight:600,
                         cursor:"pointer", minWidth:210 }}>
          {Object.keys(JUR_INFO).map(j=>(
            <option key={j} value={j}>
              {(FLAGS[j] ? FLAGS[j] + " " : "") + (JUR_INFO[j].name || j)}
            </option>
          ))}
        </select>
        {overdueCount>0 && (
          <span style={{ background:"#EF4444", color:"#fff", borderRadius:10,
                         padding:"2px 7px", fontSize:9.5, fontWeight:700 }}
                title="Obligations overdue for this office">
            {overdueCount} overdue
          </span>
        )}
        <div style={{ marginLeft:"auto", display:"flex", gap:4 }}>
          {["overview","obligations","entities","aml","legislation"].map(v=>(
            <button key={v} onClick={()=>setView(v)} style={{ padding:"5px 12px", fontSize:11, borderRadius:20, border:`0.5px solid ${view===v?"#ccc":"#e5e5e5"}`, background:view===v?"#fff":"transparent", color:view===v?"#111":"#666", cursor:"pointer", fontWeight:view===v?500:400 }}>
              {{"overview":"Overview","obligations":"Obligations","aml":"AML framework","legislation":"Legislation"}[v]}
            </button>
          ))}
        </div>

      {obligations.length===0 && (
        <div style={{ background:"#FDF4DC", border:"0.5px solid #E5CE9A", borderRadius:8,
                      padding:"12px 14px", fontSize:11.5, color:"#7B4F1D", lineHeight:1.7, margin:"8px 0 14px" }}>
          <strong>No obligation schedule recorded for {data.name} yet.</strong>
          <div style={{ marginTop:5 }}>{data.pending}</div>
        </div>
      )}
      </div>

      <div style={{ background:"#fff", minHeight:"calc(100vh - 100px)", padding:"16px 24px" }}>

        {/* OVERVIEW */}
        {view==="overview"&&(
          <div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:20 }}>
              {[
                { l:"Entities",         v:data.entities.length,                             c:CY },
                { l:"Obligations overdue", v:obligations.filter(o=>o.status==="Overdue").length, c:"#EF4444" },
                { l:"Issues flagged",   v:data.entities.reduce((s,e)=>s+e.issues,0),       c:"#F59E0B" },
                // Read from the record, not hardcoded. This previously said
                // CIMA or MFSA regardless, so Cyprus showed "MFSA" — wrong,
                // and wrong in a way that reads as authoritative.
                { l:"Regulator",        v:shortRegulator(data.regulator),                  c:"#666" },
              ].map(k=>(
                <div key={k.l} style={{ background:"#f9f9f9", borderRadius:8, padding:"12px 14px" }}>
                  <div style={{ fontSize:10, color:"#666", marginBottom:4 }}>{k.l}</div>
                  <div style={{ fontSize:20, fontWeight:700, color:k.c }}>{k.v}</div>
                </div>
              ))}
            </div>

            {/* Unconfirmed obligations came from the migration or a first
                draft. They are shown, because a visible unconfirmed deadline
                is more use than none — but they must not read as checked. */}
            {obligations.filter(x=>!x.confirmed).length>0 && (
              <div style={{ background:"#FDF4DC", border:"0.5px solid #E5CE9A", borderRadius:8,
                            padding:"10px 14px", marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#7B4F1D", marginBottom:4 }}>
                  {obligations.filter(x=>!x.confirmed).length} obligation(s) not yet confirmed
                </div>
                <div style={{ fontSize:11, color:"#7B4F1D", lineHeight:1.7 }}>
                  These are recorded but nobody has checked them against the legislation.
                  Confirming one requires its source and an owner. Until then, treat the
                  dates as a draft rather than a deadline.
                </div>
              </div>
            )}

            {obligations.length===0 && (
              <div style={{ background:"#FDF4DC", border:"0.5px solid #E5CE9A", borderRadius:8,
                            padding:"10px 14px", marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#7B4F1D", marginBottom:4 }}>
                  No obligations recorded for {data.name}
                </div>
                <div style={{ fontSize:11, color:"#7B4F1D", lineHeight:1.7 }}>
                  The tracker shows no deadlines for this jurisdiction, so nothing here will
                  fall due or be chased. Deadlines are deliberately not pre-filled: a wrong
                  date in a compliance tracker is worse than a visibly empty one.
                </div>
                <button style={{ ...nb, marginTop:8 }} onClick={()=>{setObForm(true);setOb({});}}>
                  ＋ Add an obligation
                </button>
              </div>
            )}

            {obligations.length>0 && (
              <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
                <button style={nb}
                        title="Across every jurisdiction and area at once — this screen shows one jurisdiction at a time, so the gaps between them were invisible"
                        onClick={async()=>{
                          setObsMsg("");
                          const [cov, sum] = await Promise.all([
                            OBL.obligationCoverage(), OBL.obligationSummary(),
                          ]);
                          if (!cov || !cov.live) { setObsMsg("Not signed in."); return; }
                          const rows = cov.data || [];
                          const tot = (sum && sum.data) || [];
                          const gaps = rows.filter(r => Number(r.recorded) === 0);
                          const unconf = rows.filter(r => Number(r.recorded) > 0
                                                       && Number(r.confirmed) === 0);
                          setObsMsg(
                            (gaps.length
                              ? gaps.length + " jurisdiction/area combination(s) with NOTHING recorded:\n"
                                + gaps.slice(0,14).map(r=>`  ${r.location_name} — ${r.area_name}`).join("\n")
                                + "\n\n"
                              : "Every jurisdiction and area has at least one obligation recorded.\n\n")
                            + (unconf.length
                              ? unconf.length + " with obligations recorded but NONE confirmed:\n"
                                + unconf.slice(0,10).map(r=>`  ${r.location_name} — ${r.area_name} (${r.recorded})`).join("\n")
                                + "\n\nRecorded is not the same as trustworthy — an unconfirmed deadline is a draft."
                              : "Everything recorded has been confirmed.")
                            + (tot.length
                              ? "\n\nBy jurisdiction:\n" + tot.map(t=>
                                  `  ${t.name}: ${t.areas_covered ?? t.areas ?? "?"} area(s) covered, ${t.obligations ?? t.total ?? "?"} obligation(s)`).join("\n")
                              : ""));
                        }}>
                  Coverage across all jurisdictions
                </button>
                <button style={nb} onClick={()=>{setObForm(true);setOb({});}}>
                  ＋ Add an obligation
                </button>
              </div>
            )}

            {obsMsg && (
              <div style={{ background:"#FCEBEB", border:"0.5px solid #f0c9c9", borderRadius:8,
                            padding:"9px 12px", marginBottom:14, fontSize:11.5, color:"#A32D2D",
                            whiteSpace:"pre-wrap" }}>
                {obsMsg}
              </div>
            )}

            {data.licence && (
              <div style={{ background:"#E7F4EF", border:"0.5px solid #bfe0d2", borderRadius:8,
                            padding:"10px 14px", marginBottom:16 }}>
                <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase",
                              letterSpacing:"0.4px", color:"#1F6F54", marginBottom:4 }}>
                  Licence held
                </div>
                <div style={{ fontSize:12, color:"#1F6F54" }}>{data.licence}</div>
              </div>
            )}

            {overdueCount>0&&(
              <div style={{ background:"#FCEBEB22", border:"0.5px solid #EF4444", borderRadius:8, padding:"10px 14px", marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:600, color:"#A32D2D", marginBottom:6 }}>⚠️ Overdue obligations — {data.name}</div>
                {obligations.filter(o=>o.status==="Overdue").map(o=>(
                  <div key={o.id} style={{ fontSize:11, color:"#A32D2D", display:"flex", justifyContent:"space-between", alignItems:"center", padding:"3px 0" }}>
                    <span>{o.title} · Due: {o.due} · Owner: {o.owner}</span>
                    <button style={{ ...nb, fontSize:10, borderColor:"#EF4444", color:"#EF4444" }} title="Open Statutory to action this obligation" onClick={()=>onNav&&onNav("statutory")}>Action ↗</button>
                  </div>
                ))}
              </div>
            )}

            {jur==="Malta"&&data.entities.find(e=>e.note)&&(
              <div style={{ background:"#FAEEDA22", border:"0.5px solid #F59E0B", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:12, color:"#633806" }}>
                ⚠️ <strong>Malta Ventures</strong> — unregulated Malta entity. AML/KYC onboarding not yet complete. This entity requires full compliance coverage despite being unregulated. Contact Joanne Fenech to progress.
              </div>
            )}

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <div style={{ background:"#fff", border:"0.5px solid #e5e5e5", borderRadius:10, padding:14 }}>
                <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.4px", color:"#888", marginBottom:12 }}>Regulator — {data.name}</div>
                {[["Regulator",data.regulator],["MLRO",data.amlKey.find(([k])=>k==="MLRO")?.[1]||"—"],["DMLRO",data.amlKey.find(([k])=>k.includes("DMLRO"))?.[1]||"—"],["STR filing",data.amlKey.find(([k])=>k==="STR filing body")?.[1]||"—"]].map(([k,v])=>(
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"0.5px solid #f5f5f5", fontSize:12 }}>
                    <span style={{ color:"#666" }}>{k}</span><span style={{ fontWeight:500, textAlign:"right", maxWidth:220, fontSize:11 }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ background:"#fff", border:"0.5px solid #e5e5e5", borderRadius:10, padding:14 }}>
                <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.4px", color:"#888", marginBottom:12 }}>Entity overview</div>
                {data.entities.map(e=>(
                  <div key={e.name} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:"0.5px solid #f5f5f5" }}>
                    <div>
                      <div style={{ fontSize:12, fontWeight:500 }}>{e.name}</div>
                      <div style={{ fontSize:10, color:"#aaa" }}>{e.type} · {e.administrator}</div>
                    </div>
                    <div style={{ display:"flex", gap:6 }}>
                      <Badge label={e.risk} colors={{ Low:{bg:"#EAF3DE",color:"#27500A"}, Medium:{bg:"#FAEEDA",color:"#633806"}, High:{bg:"#FCEBEB",color:"#A32D2D"}, "Very High":{bg:"#F7C1C1",color:"#501313"} }[e.risk]||{bg:"#eee",color:"#666"}} />
                      {e.issues>0&&<Badge label={e.issues+" issue"+(e.issues>1?"s":"")} colors={{ bg:"#FCEBEB", color:"#A32D2D" }} />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* OBLIGATIONS */}
        {view==="obligations"&&(
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:500 }}>Regulatory obligations — {data.name}</div>
              <button style={nba} onClick={()=>setModal("addObligation")}>＋ Add obligation</button>
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                {["Area","Obligation","Frequency","Due","Owner","Status","","Action"].map(h=><th key={h} style={th}>{h}</th>)}
              </tr></thead>
              
            <tbody>
                {obligations.map(o=>(
                  <tr key={o.id} style={{ borderBottom:"0.5px solid #f0f0f0", background:o.status==="Overdue"?"#FFF5F5":"transparent" }}>
                    <td style={td}><Badge label={o.area} colors={{ "AML/CFT":{bg:"#FCEBEB",color:"#A32D2D"}, AEOI:{bg:"#EEF0FB",color:"#3C3489"}, Substance:{bg:"#FAEEDA",color:"#633806"}, "BO register":{bg:"#EAF3DE",color:"#27500A"}, "Annual returns":{bg:"#E6F7FB",color:"#0077A8"}, "CSP licence":{bg:"#EAF3DE",color:"#27500A"}, Reporting:{bg:"#E6F7FB",color:"#0077A8"}, Funds:{bg:"#EEF0FB",color:"#3C3489"}, "Data protection":{bg:"#F1EFE8",color:"#555"} }[o.area]||{bg:"#eee",color:"#666"}} /></td>
                    <td style={{ ...td, fontWeight:500 }}>{o.title}</td>
                    <td style={{ ...td, color:"#666", fontSize:10 }}>{o.freq}</td>
                    <td style={{ ...td, color:o.status==="Overdue"?"#EF4444":"#666", fontWeight:o.status==="Overdue"?600:400 }}>{o.due}</td>
                    <td style={{ ...td, color:"#666" }}>{o.owner}</td>
                    <td style={td}><Badge label={o.status} colors={statusC[o.status]||{bg:"#eee",color:"#666"}} /></td>
                    <td style={td}>
                      {!o.confirmed && (
                        <button style={{ ...nb, fontSize:10 }}
                                title="Refused without a legislation reference and a named owner — a confirmed deadline nobody can trace cannot be checked by anyone else"
                                onClick={async()=>{
            try {
                                  setObsMsg("");
                                  const r = await OBL.obligationConfirm(o.id);
                                  if (r && r.ok) { setObsMsg("Obligation confirmed."); loadObs(); return; }
                                  setObsMsg((r && r.error) || "That could not be confirmed.");
            } catch (e) {
              // Was silent: the call failed and nothing was said, so the
              // button looked dead. A refusal from the database is usually
              // the most useful sentence on the screen.
              window.alert(messageFor(e));
            }
          }}>
                          Confirm
                        </button>
                      )}
                      <button style={{ ...nb, fontSize:10, marginRight:4 }}
                              title="Amending a confirmed obligation withdraws the confirmation — whoever confirmed it confirmed different terms"
                              onClick={async()=>{
            try {
                                if (o.confirmed && !window.confirm(
                                  "This obligation is CONFIRMED.\n\nAmending it withdraws the confirmation, because whoever confirmed it confirmed different terms. It will need confirming again.\n\nContinue?")) return;
                                const t = window.prompt("Obligation title?", o.title || "");
                                if (t == null) return;
                                const leg = window.prompt("Legislation or rule reference?", o.legislation || "");
                                const own = window.prompt("Owner?", o.owner === "—" ? "" : o.owner);
                                setObsMsg("");
                                const r = await OBL.obligationUpdate(o.id, {
                                  title: t, legislationRef: leg, owner: own });
                                if (r && r.ok) { setObsMsg("Obligation amended."); loadObs(); return; }
                                setObsMsg((r && r.error) || "That could not be amended.");
            } catch (e) {
              // Was silent: the call failed and nothing was said, so the
              // button looked dead. A refusal from the database is usually
              // the most useful sentence on the screen.
              window.alert(messageFor(e));
            }
          }}>
                        Edit
                      </button>
                      {o.confirmed && (
                        <button style={{ ...nb, fontSize:10, color:"#A32D2D", borderColor:"#f0c9c9" }}
                                title="Removing needs a reason and deactivates rather than deletes — a schedule that used to include something is part of the compliance history"
                                onClick={async()=>{
            try {
                                  const why = window.prompt("Why is this obligation being removed?");
                                  if (!why) return;
                                  setObsMsg("");
                                  const r = await OBL.obligationRemove(o.id, why);
                                  if (r && r.ok) { setObsMsg("Obligation deactivated."); loadObs(); return; }
                                  setObsMsg((r && r.error) || "That could not be removed.");
            } catch (e) {
              // Was silent: the call failed and nothing was said, so the
              // button looked dead. A refusal from the database is usually
              // the most useful sentence on the screen.
              window.alert(messageFor(e));
            }
          }}>
                          Remove
                        </button>
                      )}
                    </td>
                    <td style={td}>{o.status==="Overdue"?<button style={{ ...nb, fontSize:10, borderColor:"#EF4444", color:"#EF4444" }} onClick={openPortal}>File ↗</button>:<button style={{ ...nb, fontSize:10 }} title="View this obligation in Statutory" onClick={()=>onNav&&onNav("statutory")}>View ↗</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ENTITIES */}
        {view==="entities"&&(
          <div>
            <div style={{ fontSize:12, fontWeight:500, marginBottom:14 }}>Entities — {data.name} ({data.entities.length})</div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                {["Entity","Type","Risk","Administrator","Issues","Action"].map(h=><th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {data.entities.map(e=>(
                  <tr key={e.name} style={{ borderBottom:"0.5px solid #f0f0f0", background:e.issues>0?"#FFFBEB":"transparent" }}>
                    <td style={{ ...td, fontWeight:600 }}>
                      {e.name}
                      {e.note&&<div style={{ fontSize:9, color:"#F59E0B", fontWeight:400 }}>⚠️ {e.note}</div>}
                    </td>
                    <td style={{ ...td, color:"#666" }}>{e.type}</td>
                    <td style={td}><Badge label={e.risk} colors={{ Low:{bg:"#EAF3DE",color:"#27500A"}, Medium:{bg:"#FAEEDA",color:"#633806"}, High:{bg:"#FCEBEB",color:"#A32D2D"}, "Very High":{bg:"#F7C1C1",color:"#501313"} }[e.risk]||{bg:"#eee",color:"#666"}} /></td>
                    <td style={{ ...td, color:"#666" }}>{e.administrator}</td>
                    <td style={td}>{e.issues>0?<Badge label={e.issues+" open"} colors={{ bg:"#FCEBEB", color:"#A32D2D" }} />:<span style={{ color:"#4CAF7D", fontSize:11 }}>✓ None</span>}</td>
                    <td style={td}><button style={{ ...nb, fontSize:10 }} onClick={()=>onNav&&onNav("entities")}>Open in Entity Admin ↗</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* AML FRAMEWORK */}
        {view==="aml"&&(
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div style={{ background:"#fff", border:"0.5px solid #e5e5e5", borderRadius:10, padding:16 }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.4px", color:"#888", marginBottom:12 }}>AML/CFT framework — {data.name}</div>
              {data.amlKey.map(([k,v])=>(
                <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"0.5px solid #f5f5f5", fontSize:12 }}>
                  <span style={{ color:"#666" }}>{k}</span><span style={{ fontWeight:500, textAlign:"right", maxWidth:220, fontSize:11 }}>{v}</span>
                </div>
              ))}
            </div>
            <div style={{ background:"#fff", border:"0.5px solid #e5e5e5", borderRadius:10, padding:16 }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.4px", color:"#888", marginBottom:12 }}>Jurisdiction-specific requirements</div>
              {jur==="Cayman"?[
                ["CIMA supervision tier","Tier B — Corporate / Trust Service Provider"],
                ["AML regulations","Anti-Money Laundering Regulations (as revised)"],
                ["CDD standard","Full CDD for all clients — no simplified available for CSPs"],
                ["PEP screening","Enhanced — all clients regardless of risk"],
                ["EDD requirement","All PEPs, HNW clients, and High/VH risk clients"],
                ["Transaction monitoring","Manual for now; Copilot integration Phase 2"],
                ["STR threshold","No minimum — file on suspicion"],
                ["Record retention","5 years post relationship end (minimum)"],
              ].map(([k,v])=>(
                <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"0.5px solid #f5f5f5", fontSize:12 }}>
                  <span style={{ color:"#666" }}>{k}</span><span style={{ fontWeight:500, textAlign:"right", maxWidth:220, fontSize:11 }}>{v}</span>
                </div>
              )):[
                ["FIAU supervision","Regulated as CSP and TrustCo"],
                ["AML regulations","PMLA and FIAU Implementing Procedures Part II"],
                ["CDD standard","Full CDD — all clients"],
                ["Simplified CDD","Not available for CSPs under Maltese law"],
                ["PEP screening","All clients — Enhanced where PEP status identified"],
                ["EDD requirement","All PEPs, cross-border correspondent relationships"],
                ["STR threshold","No minimum — file on reasonable suspicion"],
                ["Record retention","5 years from end of business relationship (Art. 28 PMLA)"],
                ["FIAU reporting","Annual supervisory report — due 30 April"],
                ["Data Protection","GDPR applies — DPO appointed — see System Admin"],
              ].map(([k,v])=>(
                <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"0.5px solid #f5f5f5", fontSize:12 }}>
                  <span style={{ color:"#666" }}>{k}</span><span style={{ fontWeight:500, textAlign:"right", maxWidth:220, fontSize:11 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LEGISLATION */}
        {view==="legislation"&&(
          <div>
            <div style={{ fontSize:12, fontWeight:500, marginBottom:14 }}>Applicable legislation — {data.name}</div>
            <div style={{ background:"#fff", border:"0.5px solid #e5e5e5", borderRadius:10, padding:16 }}>
              {data.legislation.map((l,i)=>(
                <div key={i} style={{ display:"flex", gap:12, padding:"9px 0", borderBottom:"0.5px solid #f5f5f5", alignItems:"center" }}>
                  <span style={{ width:24, height:24, borderRadius:"50%", background:"#E6F7FB", color:CY, fontSize:11, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{i+1}</span>
                  <span style={{ fontSize:12, fontWeight:500 }}>{l}</span>
                  <button style={{ ...nb, fontSize:10, marginLeft:"auto", flexShrink:0 }} title="View this obligation in Statutory" onClick={()=>onNav&&onNav("statutory")}>View ↗</button>
                </div>
              ))}
            </div>
            <div style={{ marginTop:14, background:"#f9f9f9", borderRadius:8, padding:"10px 14px", fontSize:11, color:"#666" }}>
              ℹ️ Legislation references are updated by the Head of Compliance as and when amendments are enacted. Changes that require operational action are flagged as compliance events. Contact Colette Grisdale with any queries.
            </div>
          </div>
        )}
      </div>

      {modal&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100 }} onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div style={{ background:"#fff", borderRadius:12, padding:24, width:440, maxWidth:"95vw" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600 }}>Add regulatory obligation</h3>
              <button onClick={()=>setModal(null)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#888" }}>×</button>
            </div>
            {[["Area","select",["AML/CFT","AEOI","Substance","BO register","Annual returns","Reporting","Other"]],["Title","text"],["Frequency","select",["Annual","Quarterly","Monthly","On change","Ongoing"]],["Due date","date"],["Owner","select",["Colette Grisdale","Garry Crossan","Joanne Fenech","Andy Morgan"]]].map(([l,t,opts])=>(
              <div key={l} style={{ marginBottom:12 }}>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#555", marginBottom:4 }}>{l}</label>
                {t==="select"?<select style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #e0e0e0", borderRadius:6, fontSize:12, outline:"none" }}>{(opts||[]).map(o=><option key={o}>{o}</option>)}</select>
                :<input type={t} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #e0e0e0", borderRadius:6, fontSize:12, outline:"none", boxSizing:"border-box" }} />}
              </div>
            ))}
            <button onClick={()=>{ window.alert("This form saves nothing. Obligations are added and amended from the obligations table below, which does save."); setModal(null); }} style={{ width:"100%", background:CY, color:"#fff", border:"none", borderRadius:8, padding:10, fontSize:13, fontWeight:600, cursor:"pointer" }}>Save</button>
          </div>
        </div>
      )}

      {/* Entering an obligation. The trigger is asked for separately from the
          deadline, because "30 days" recorded without saying 30 days from what
          is the commonest way a compliance date goes wrong. */}
      {obForm && (
        <div onClick={(e)=>e.target===e.currentTarget&&(setObForm(false),setOb({}))}
             style={{ position:"fixed", inset:0, background:"rgba(0,18,66,0.45)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      zIndex:1000, padding:20 }}>
          <div style={{ background:"#fff", borderRadius:12, padding:"22px 24px",
                        width:"min(640px,100%)", maxHeight:"86vh", overflowY:"auto" }}>
            <div style={{ fontSize:15, fontWeight:600, color:"#001242", marginBottom:6 }}>
              Add an obligation — {data.name}
            </div>
            <div style={{ fontSize:11.5, color:"#5B6B7B", lineHeight:1.7, marginBottom:14 }}>
              The trigger and the deadline are separate: the same "30 days" means a different
              date depending on what it runs from. Record the legislation and an owner too —
              both are required before the obligation can be confirmed.
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px 14px" }}>
              {[
                { k:"area",  label:"Area", type:"select", opts:OBL.AREAS.map(a=>a.id+" — "+a.label), req:true },
                { k:"title", label:"Obligation", full:true, req:true, ph:"Annual return — Companies Registry" },
                { k:"triggerType", label:"Deadline runs from", type:"select",
                  opts:OBL.TRIGGER_TYPES.map(t=>t.id+" — "+t.label), req:true },
                { k:"triggerDetail", label:"Trigger detail", ph:"e.g. incorporation date" },
                { k:"dueDays",   label:"Days after" },
                { k:"dueMonths", label:"or Months after" },
                { k:"fixedMonth",label:"Fixed month (1-12)" },
                { k:"fixedDay",  label:"Fixed day (1-31)" },
                { k:"frequency", label:"Frequency", ph:"Annual" },
                { k:"owner",     label:"Owner", ph:"Who does it" },
                { k:"legislationRef", label:"Legislation or rule", full:true,
                  ph:"Companies Act 2006 s.120 — required before confirming" },
                { k:"filingRoute", label:"How it is filed", full:true, ph:"Portal, form number or agent" },
                { k:"appliesTo", label:"Applies to", full:true, ph:"Which entities" },
              ].map(f=>(
                <div key={f.k} style={{ gridColumn:f.full?"1/-1":"auto" }}>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#555",
                                  marginBottom:4 }}>
                    {f.label}{f.req&&<span style={{ color:"#A32D2D" }}> *</span>}
                  </label>
                  {f.type==="select" ? (
                    <select value={ob[f.k]||""} onChange={e=>setOb({...ob,[f.k]:e.target.value})}
                            style={{ width:"100%", height:34, fontSize:12.5, borderRadius:6,
                                     border:"0.5px solid #ccc", padding:"0 8px" }}>
                      <option value="">—</option>
                      {f.opts.map(x=><option key={x} value={x}>{x}</option>)}
                    </select>
                  ) : (
                    <input value={ob[f.k]||""} placeholder={f.ph||""}
                           onChange={e=>setOb({...ob,[f.k]:e.target.value})}
                           style={{ width:"100%", height:34, fontSize:12.5, borderRadius:6,
                                    border:"0.5px solid #ccc", padding:"0 8px" }} />
                  )}
                </div>
              ))}
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:18 }}>
              <button style={nb} onClick={()=>{setObForm(false);setOb({});}}>Cancel</button>
              <button style={{ ...nb, background:CY, color:"#fff", border:"none" }}
                      onClick={async()=>{
            try {
                        const res = await OBL.obligationAdd({
                          location: LOC_CODE[jur], area:(ob.area||"").split(" ")[0],
                          title: ob.title,
                          triggerType:(ob.triggerType||"").split(" ")[0],
                          triggerDetail: ob.triggerDetail,
                          dueDays: ob.dueDays?Number(ob.dueDays):null,
                          dueMonths: ob.dueMonths?Number(ob.dueMonths):null,
                          fixedMonth: ob.fixedMonth?Number(ob.fixedMonth):null,
                          fixedDay: ob.fixedDay?Number(ob.fixedDay):null,
                          frequency: ob.frequency, appliesTo: ob.appliesTo,
                          filingRoute: ob.filingRoute, legislationRef: ob.legislationRef,
                          owner: ob.owner,
                        });
                        if (res && res.ok) { setObForm(false); setOb({}); loadObs(); return; }
                        if (res && res.live===false) { setObsMsg("Not signed in — this cannot be saved yet."); return; }
                        setObsMsg((res&&res.error)||"That could not be saved.");
            } catch (e) {
              // Was silent: the call failed and nothing was said, so the
              // button looked dead. A refusal from the database is usually
              // the most useful sentence on the screen.
              window.alert(messageFor(e));
            }
          }}>
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
